import { useCallback, useMemo, useState } from "react";
import useStudentPortalFetch from "./useStudentPortalFetch";

const STORAGE_KEY = "ems_student_selected_admission_id";

function readStoredAdmissionId() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw == null || raw === "" || raw === "all") return "all";
    return String(raw);
  } catch {
    return "all";
  }
}

function courseLabel(row) {
  const program =
    row?.training_program || row?.trainingProgram || {};
  const name =
    program.training_program_name ||
    row?.training_program_name ||
    "Course";
  const batch =
    row?.batch?.batch_name ||
    row?.batch?.batch_code ||
    row?.batch_name ||
    "";
  return batch ? `${name} · ${batch}` : name;
}

function normalizeId(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isNaN(n) && n > 0) return String(n);
  const s = String(value).trim();
  return s || null;
}

/**
 * Shared course selector state for student portal pages.
 * Selection is keyed by admission id and persisted in localStorage.
 */
export default function useStudentCourseSelection() {
  const { data, loading, error } = useStudentPortalFetch("courses");
  const admissions = Array.isArray(data) ? data : [];

  const options = useMemo(
    () =>
      admissions.map((row) => {
        const admissionId = normalizeId(row.organization_ems_admission_id);
        const batchId = normalizeId(
          row.batch?.organization_ems_batch_id ||
            row.organization_ems_batch_id
        );
        const programId = normalizeId(
          row.organization_ems_training_program_id ||
            row.training_program_id ||
            row.training_program?.organization_ems_training_program_id ||
            row.trainingProgram?.organization_ems_training_program_id
        );
        return {
          key: admissionId || `course-${courseLabel(row)}`,
          admissionId,
          batchId,
          programId,
          label: courseLabel(row),
        };
      }),
    [admissions]
  );

  const [selectedAdmissionId, setSelectedAdmissionIdState] = useState(
    readStoredAdmissionId
  );

  const setSelectedAdmissionId = useCallback((value) => {
    const next = value == null || value === "" ? "all" : String(value);
    setSelectedAdmissionIdState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const selected = useMemo(() => {
    if (selectedAdmissionId === "all") return null;
    return (
      options.find((opt) => opt.admissionId === selectedAdmissionId) || null
    );
  }, [options, selectedAdmissionId]);

  const showFilter = options.length > 1;

  const matchesSelection = useCallback(
    (row) => {
      if (!selected) return true;
      if (!row || typeof row !== "object") return false;

      const rowAdmission = normalizeId(
        row.organization_ems_admission_id ||
          row.admission_id ||
          row.admission?.organization_ems_admission_id
      );
      if (selected.admissionId && rowAdmission) {
        return rowAdmission === selected.admissionId;
      }

      const rowBatch = normalizeId(
        row.organization_ems_batch_id ||
          row.batch_id ||
          row.batch?.organization_ems_batch_id
      );
      if (selected.batchId && rowBatch) {
        return rowBatch === selected.batchId;
      }

      const rowProgram = normalizeId(
        row.organization_ems_training_program_id ||
          row.training_program_id ||
          row.training_program?.organization_ems_training_program_id
      );
      if (selected.programId && rowProgram) {
        return rowProgram === selected.programId;
      }

      const rowName = String(
        row.training_program_name ||
          row.courseLabel ||
          row.admission?.training_program?.training_program_name ||
          ""
      ).trim();
      if (rowName && selected.label) {
        return (
          selected.label === rowName ||
          selected.label.startsWith(`${rowName} ·`) ||
          rowName === selected.label.split(" · ")[0]
        );
      }

      return false;
    },
    [selected]
  );

  const filterRows = useCallback(
    (rows) => {
      if (!Array.isArray(rows)) return [];
      if (!selected) return rows;
      return rows.filter(matchesSelection);
    },
    [matchesSelection, selected]
  );

  return {
    options,
    selected,
    selectedAdmissionId: showFilter ? selectedAdmissionId : "all",
    setSelectedAdmissionId,
    showFilter,
    loading,
    error,
    matchesSelection,
    filterRows,
  };
}
